import { useEffect, useState } from "react";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { Icon } from "../../../../../components/icon/icon";
import { ViewPayoutModal } from "../../../../../components/modal/app_modals/viewPayoutModal";
import { Pill } from "../../../../../components/pill";
import { TableComponent } from "../../../../../components/table";
import { MiniHeader } from "../../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../../customeHooks/useModalControl";
import { useAdminGetEventTicketsBreakdownQuery } from "../../../../../redux/features/admin/event/events.api";
import {
  useAdminGetPayoutHistoryQuery,
  useAdminGetPurchaseStatisticsQuery,
} from "../../../../../redux/features/admin/payments/payments.api";
import {
  CreatedEvent,
  CreateTicket,
  GetEventTicketsBreakdownResponse,
  GetEventTicketsResponse,
} from "../../../../../redux/features/event/event.types";
import {
  PayoutCurrency,
  SinglePayout,
} from "../../../../../redux/features/payment/payment.types";
import {
  currencyShorts,
  paidTooltip,
  pendingPayoutTooltip,
} from "../../../../../utils/constants";
import {
  formatDate,
  formatToCurrency,
  getCurrencyShort,
} from "../../../../../utils/helpers";
import { QueryParams } from "../../../../../utils/types";

const SingleTicket = ({
  ticket,
  ticketPurchaseBreakdown,
}: {
  ticket: CreateTicket & {
    _id: string;
    quantity: number;
  };
  ticketPurchaseBreakdown?: GetEventTicketsBreakdownResponse;
}) => {
  return (
    <div key={`${ticket._id}`} className="flex items-center gap-4">
      <Icon svg="ticket" width={20} height={20} className="text-grey-500" />

      <div className="flex-1 text-sm text-grey-900">
        <p className="text-base font-bold">{ticket.name}</p>
        <p className="capitalize">
          {ticket.ticketType === "free"
            ? "Free"
            : formatToCurrency(ticket.price!, {
                decimalPlaces: 2,
                currency: getCurrencyShort(ticket.currency),
              })}
        </p>
        <p className="">
          {ticket.quantityType === "unlimited"
            ? `${ticketPurchaseBreakdown?.data?.find((tpb) => tpb.ticketId === ticket._id)?.totalQuantityPurchased ?? 0} sold • Unlimited`
            : `${ticketPurchaseBreakdown?.data?.find((tpb) => tpb.ticketId === ticket._id)?.totalQuantityPurchased ?? 0}/${ticket.quantity} registered`}
        </p>
      </div>
    </div>
  );
};

export const AdminEventTickets = ({
  event,
  data,
  fetchingTickets,
  eventCurrency,
}: {
  event: CreatedEvent;
  data?: GetEventTicketsResponse;
  fetchingTickets?: boolean;
  eventCurrency: PayoutCurrency;
}) => {
  const [hasPaidTickets, setHasPaidTickets] = useState(false);
  const [eventTickets, setEventTickets] = useState<
    (CreateTicket & { _id: string; quantity: number })[]
  >([]);
  const [selectedPayout, setSelectedPayout] = useState<SinglePayout>();
  const [payoutParams, setPayoutParams] = useState<QueryParams>({
    page: 1,
    limit: 10,
  });

  const { handleModalClose, handleModalOpen, modal, modalOpen } =
    useModalControl();

  const { data: ticketPurchaseBreakdown } =
    useAdminGetEventTicketsBreakdownQuery(
      {
        id: event._id,
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: payoutHistory, isFetching: fetchingPayoutHistory } =
    useAdminGetPayoutHistoryQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: statistics, isFetching: fetchingStatistics } =
    useAdminGetPurchaseStatisticsQuery(
      {
        eventId: event._id,
      },
      { refetchOnMountOrArgChange: true, skip: !hasPaidTickets },
    );

  useEffect(() => {
    if (data?.data) {
      setEventTickets(
        data.data.tickets.map((t) => {
          return {
            ...t,
          };
        }),
      );
    }

    setHasPaidTickets(
      data?.data?.tickets?.map((t) => t.ticketType).includes("paid") ?? false,
    );
  }, [data]);

  return (
    <>
      <BgWrapper className="flex flex-col gap-3 !rounded-t-none">
        <MiniHeader header="Tickets" />

        {hasPaidTickets && (
          <div className="grid grid-cols-1 gap-[10px] md:grid-cols-3">
            <NumberCard
              value={statistics?.data.totalAmount ?? 0}
              text="Total Tickets Sold"
              loading={fetchingStatistics}
              currency={eventCurrency}
            />

            <NumberCard
              value={statistics?.data.totalSuccessfulPayments ?? 0}
              text="Total Amount Paid"
              tooltipId={paidTooltip.id}
              tooltipText={paidTooltip.text}
              loading={fetchingStatistics}
              currency={eventCurrency}
            />

            <NumberCard
              value={statistics?.data.totalPendingPayments ?? 0}
              text="Pending Payouts"
              tooltipId={pendingPayoutTooltip.id}
              tooltipText={pendingPayoutTooltip.text}
              loading={fetchingStatistics}
              currency={eventCurrency}
            />
          </div>
        )}

        <TableComponent
          headers={[]}
          rows={eventTickets.map((ticket, idx) => {
            return {
              id: idx,
              items: [
                <SingleTicket
                  key={`${ticket._id}`}
                  ticket={ticket}
                  ticketPurchaseBreakdown={ticketPurchaseBreakdown}
                />,
              ],
            };
          })}
          isLoading={fetchingTickets}
          isRowClickable={false}
          isTableEmpty={eventTickets.length === 0}
          emptyTitle="No Tickets Created"
          emptyImage="/assets/images/ticket-glass.png"
          emptySubTitle="When you add tickets they would show up here."
          loadingRows={3}
          loadingHeight={63}
        />
      </BgWrapper>

      <BgWrapper className="flex flex-col gap-3">
        <div className="mt-3 flex flex-col gap-3">
          <MiniHeader header="Payout History" />

          <TableComponent
            headers={[]}
            rows={
              payoutHistory?.data?.data?.map((payout, idx) => {
                return {
                  id: idx,
                  items: [
                    <div className="flex items-center gap-0 text-sm text-grey-600">
                      <p className="font-bold text-grey-900">
                        {formatToCurrency(payout.amount, {
                          decimalPlaces: 2,
                          currency: currencyShorts[eventCurrency],
                        })}
                      </p>{" "}
                      {payout.payoutAccount ? (
                        <p className="flex-1">
                          • {payout.ticketCount}{" "}
                          {payout.ticketCount === 1 ? "Ticket" : "Tickets"} Sold
                        </p>
                      ) : (
                        <p className="flex-1"></p>
                      )}
                      <div className="flex items-center gap-2">
                        <p className="ml-2">
                          {payout.status === "pending"
                            ? "To be paid on"
                            : "Paid on"}{" "}
                          {formatDate(
                            payout.status === "pending"
                              ? payout.payoutDate
                              : payout.payoutDate,
                            "ddd, MMMM DD, YYYY",
                          )}
                        </p>

                        <Pill
                          text={
                            payout.status === "pending" ? "PENDING" : "PAID"
                          }
                          variation={
                            payout.status === "pending" ? "yellow" : "green"
                          }
                        />
                      </div>
                    </div>,
                  ],
                };
              }) ?? []
            }
            onRowClick={(idx) => {
              setSelectedPayout(payoutHistory?.data?.data![idx]);
              handleModalOpen("viewPayoutModal");
            }}
            isRowClickable={true}
            isTableEmpty={[0, undefined].includes(
              payoutHistory?.data?.data?.length,
            )}
            emptyTitle="No Payouts Made Yet"
            emptySubTitle=" You get paid at the end of everyday once you’ve made ticket sales"
            emptyImage="/assets/images/bank-glass.png"
            isLoading={fetchingPayoutHistory}
            isPaginated={true}
            currentPage={payoutParams.page}
            totalCount={payoutHistory?.data?.totalCount ?? 0}
            itemsPerPage={payoutParams.limit}
            onPageChange={(page) => {
              setPayoutParams({
                ...payoutParams,
                page: page,
              });
            }}
          />
        </div>
      </BgWrapper>

      {selectedPayout && (
        <ViewPayoutModal
          open={modalOpen && modal === "viewPayoutModal"}
          handleClose={() => {
            handleModalClose();
            setSelectedPayout(undefined);
          }}
          payout={selectedPayout}
          eventId={event?._id ?? ""}
          adminView={true}
          currency={eventCurrency}
        />
      )}
    </>
  );
};
